import { ProtectedRoute } from "components/ProtectedRoute";
import Dashboard from "pages/Dashboard";
import PerformanceRedirect from "pages/org-performance";
import Financials from "pages/financials";
import CRMPage from "pages/crm";
import FormsPage from "pages/forms";
import FormResponsesPage from "pages/forms/FormResponsesPage";
import FormBuilder from "pages/forms/FormBuilder";
import MessageTemplatesPage from "pages/message-templates";
import TemplateEditor from "pages/message-templates/TemplateEditor";
import AutomationsRoute from "pages/automations";
import ClassesPage from "pages/classes";
import PublicFormPage from "pages/public-form";
import UnsubscribePage from "pages/unsubscribe";
import ConfirmEmailPage from "pages/confirm-email";
import StudentDetail from "pages/manage/students/[id]";
import TutorDetail from "pages/manage/tutors/[id]";
import TutorAvailability from "pages/tutors/availability";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import StripeKYC from "components/Signup/StripeKYC";
import PaymentStatus from "components/PaymentMethods/PaymentMethodStatus";
import ParentDetail from "pages/manage/parents/[id]";
import MainLayout from "components/Routes/MainLayout";
import Error404 from "pages/Error404";
import PaymentMethodSuccess from "components/RequestPaymentMethod/PaymentMethodSuccess";
import Signup from "pages/Signup";
import PrivacyPolicy from "pages/legal/PrivacyPolicy";
import TermsOfService from "pages/legal/TermsOfService";
import LandingPage from "pages/Landing";
import AcceptInvite from "pages/invite/accept/auth0Secret";
import InviteToken from "pages/invite/[token]";
import OrganizationWebPage from "./pages/WebPages/OrganizationWebPage";
import PublicBookingLayout from "components/Routes/PublicBookingLayout";
import TutorWebPage from "pages/WebPages/TutorWebPage";
import { isWhiteLabelHost } from "util/whiteLabel";

function SignupRedirect() {
  const { isAuthenticated, isLoading } = useAuth0();
  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/join/t4dp0l3-x9k2m7w1" replace />;
  return <Navigate to="/" replace />;
}

const AppRoutes = () => {
  const host = window.location.host;

  return (
    <Routes>
      {/* The signed-in app's pages: the navbar shows its people search and
          bell here (to admins), never on the public pages below. */}
      <Route element={<MainLayout inApp />}>
        {/* Dashboard */}
        <Route
          path="/dashboard"
          element={<ProtectedRoute component={Dashboard} />}
        />

        {/* Organization pages (reached from the profile menu) */}
        <Route
          path="/financials"
          element={<ProtectedRoute component={Financials} />}
        />
        <Route path="/crm" element={<ProtectedRoute component={CRMPage} />} />
        <Route
          path="/forms"
          element={<ProtectedRoute component={FormsPage} />}
        />
        <Route
          path="/forms/:formId"
          element={<ProtectedRoute component={FormResponsesPage} />}
        />
        <Route
          path="/forms/:formId/edit"
          element={<ProtectedRoute component={FormBuilder} />}
        />
        <Route
          path="/message-templates"
          element={<ProtectedRoute component={MessageTemplatesPage} />}
        />
        <Route
          path="/message-templates/:templateId"
          element={<ProtectedRoute component={TemplateEditor} />}
        />
        <Route
          path="/automations"
          element={<ProtectedRoute component={AutomationsRoute} />}
        />
        {/* On an org's own domain, /classes is the public Classes tab. */}
        {!isWhiteLabelHost(host) && (
          <Route
            path="/classes"
            element={<ProtectedRoute component={ClassesPage} />}
          />
        )}

        {/* Old Performance links land on the matching page */}
        <Route path="/performance" element={<PerformanceRedirect />} />

        {/* The link that confirms an admin's personal email address */}
        <Route
          path="/confirm-email/:token"
          element={<ProtectedRoute component={ConfirmEmailPage} />}
        />

        {/* Student, Tutor, and Parent Management */}
        <Route
          path="/manage/students/:id"
          element={<ProtectedRoute component={StudentDetail} />}
        />

        <Route
          path="/manage/tutors/:id"
          element={<ProtectedRoute component={TutorDetail} />}
        />

        <Route
          path="/manage/parents/:id"
          element={<ProtectedRoute component={ParentDetail} />}
        />

        {/* The old page editor: now Organization Settings > Booking pages and
            the tutor page's Booking page tab. */}
        <Route
          path="/manage/webpage"
          element={<Navigate to="/dashboard" replace />}
        />

        {/* Edit Tutor Availability */}
        <Route
          path="/availability"
          element={<ProtectedRoute component={TutorAvailability} />}
        />

        {/* KYC */}
        <Route
          path="/KYC/:type"
          element={<ProtectedRoute component={StripeKYC} />}
        />

        {/* Payment Status */}
        <Route
          path="/manage/students/payment-status"
          element={
            <ProtectedRoute
              component={PaymentStatus}
              componentProps={{
                initiatedByAdminFromOrgDashboardForStudent: true,
              }}
            />
          }
        />

        <Route
          path="/manage/parents/payment-status"
          element={
            <ProtectedRoute
              component={PaymentStatus}
              componentProps={{
                initiatedByAdminFromOrgDashboardForParent: true,
              }}
            />
          }
        />

        <Route
          path="/dashboard/payment-status"
          element={
            <ProtectedRoute
              component={PaymentStatus}
              componentProps={{ addedByStudentOrParentDirectly: true }}
            />
          }
        />
      </Route>

      <Route element={<MainLayout />}>
        {/*Marketing*/}
        <Route path="/" element={<LandingPage />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        {/* Sign up: Auth0 callback lands here, redirects authenticated users to the hidden page */}
        <Route path="/signup" element={<SignupRedirect />} />
        {/* Sign up (hidden invite-only link) */}
        <Route path="/join/t4dp0l3-x9k2m7w1" element={<Signup />} />

        {/* Invites */}
        <Route path="/invites/:token" element={<InviteToken />} />
        <Route path="/invites/accept/:auth0Secret" element={<AcceptInvite />} />

        {/* Payment Method Success */}
        <Route
          path="/payment_method_success"
          element={<PaymentMethodSuccess />}
        />

        {/* ERRORS */}
        <Route path="*" element={<Error404 />} />
      </Route>
      {/* ---------- PUBLIC BOOKING PAGES ---------- */}

      <Route element={<PublicBookingLayout />}>
        {/* Routes that exist **only** on a customer’s custom domain */}
        {isWhiteLabelHost(host) && (
          <>
            <Route index element={<OrganizationWebPage />} />
            <Route
              path="classes"
              element={<OrganizationWebPage tab="classes" />}
            />
            <Route path=":tutorSlug" element={<TutorWebPage />} />
          </>
        )}

        {/* A form someone was sent (no login) */}
        <Route path="f/:token" element={<PublicFormPage />} />
        {/* A promotional email's unsubscribe link (no login) */}
        <Route path="unsubscribe/:token" element={<UnsubscribePage />} />
        <Route path="organizations/:id" element={<OrganizationWebPage />} />
        <Route
          path="organizations/:id/classes"
          element={<OrganizationWebPage tab="classes" />}
        />
        <Route path="tutors/:id" element={<TutorWebPage />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
